import {CognitoUser} from '@aws-amplify/auth'
import {CognitoUserAttribute} from 'amazon-cognito-identity-js'
import {
  COGNITO_SESSION_EXPIRATION_ATTR,
  parseSessionExpirationHours,
} from '../../../../shared/utils/sessionExpirationPreference'

export interface CognitoSignUpModel {
  username: string
  password: string
  attributes?: {
    email?: string
    phone_number?: string
  }
}

export interface UserModel {
  userSub: string
  username: string
  password: string | undefined
  email: string
  first_name: string
  last_name: string
  fullname?: string
  occupation?: string
  companyName?: string
  phone?: string
  roles?: Array<number>
  pic?: string
  language?: 'en' | 'de' | 'es' | 'fr' | 'ja'
  timeZone?: string
  auth?: CognitoUser
}

export interface UserAttributes {
  sub?: string
  email?: string
  email_verified?: boolean
  name?: string
  familyName?: string
  updated_at?: string
  identity_id?: string
  notifEmailJobStatus?: boolean
  picture?: string // Google profile picture URL
  /** From Cognito `custom:session_expiration` (Number): hours since sign-in; 0 = no extra limit. */
  sessionExpirationHours?: number
}

export type AuthChallengeName =
  | 'NEW_PASSWORD_REQUIRED'
  | 'SMS_MFA'
  | 'SOFTWARE_TOKEN_MFA'
  | 'MFA_SETUP'

export type AuthUser = CognitoUser & {
  challengeName: AuthChallengeName
}

export type CustomCognitoUser = CognitoUser & {
  userId?: string
}

// Function to map Cognito attributes to UserAttributes
export const mapCognitoAttributesToUserAttributes = async (
  cognitoUser: CognitoUser
): Promise<UserAttributes> => {
  const getUserAttributeWrapper = (cognitoUser: CognitoUser) =>
    new Promise<CognitoUserAttribute[] | undefined>((resolve, reject) => {
      cognitoUser.getUserAttributes((err, result) => {
        if (err) {
          reject(err)
        } else {
          resolve(result)
        }
      })
    })

  let attributes: CognitoUserAttribute[] | undefined = []
  try {
    attributes = await getUserAttributeWrapper(cognitoUser)
  } catch (error) {
    throw new Error('Could not get user attributes')
  }

  const pictureAttribute = attributes!.find((attribute: any) => attribute.getName() === 'picture')
  const sessionExpRaw = attributes!
    .find((attribute: any) => attribute.getName() === COGNITO_SESSION_EXPIRATION_ATTR)
    ?.getValue()
  const sessionExpirationHours =
    sessionExpRaw === undefined || sessionExpRaw === ''
      ? undefined
      : parseSessionExpirationHours(sessionExpRaw)

  const result = {
    email: attributes!.find((attribute: any) => attribute.getName() === 'email')?.getValue() || '',
    name:
      attributes!.find((attribute: any) => attribute.getName() === 'given_name')?.getValue() || '',
    familyName:
      attributes!.find((attribute: any) => attribute.getName() === 'family_name')?.getValue() || '',
    identity_id:
      attributes!
        .find((attribute: any) => attribute.getName() === 'custom:identityId')
        ?.getValue() || '',
    email_verified:
      attributes!.find((attribute: any) => attribute.getName() === 'email_verified')?.getValue() ===
      'true',
    sub: attributes!.find((attribute: any) => attribute.getName() === 'sub')?.getValue() || '',
    updated_at:
      attributes!.find((attribute: any) => attribute.getName() === 'updated_at')?.getValue() || '',
    notifEmailJobStatus:
      Number(
        attributes!
          .find((attribute: any) => attribute.getName() === 'custom:notifEmailJobStatus')
          ?.getValue()
      ) === 1,
    picture: pictureAttribute?.getValue() || '',
    sessionExpirationHours,
  }

  return result
}
